/*
 * Collection SEO copy -- show a set number of lines, then either expand in
 * place or open the whole text in a small centred popup (data-popup).
 *
 * The whole text is already in the page; this only limits its visible height.
 * The limit is applied here rather than in the markup, so without JavaScript
 * the text simply shows in full. Whether anything is cut off is measured, not
 * guessed: the same copy overflows on a phone and fits on a desktop, and a
 * "Read more" that reveals nothing is worse than no button.
 *
 * The popup is filled by cloning the text on first open, so the copy is in
 * the HTML only once (no duplicate text for crawlers).
 */
class CollectionSeoText extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-collection-seo-body]');
    this.button = this.querySelector('[data-collection-seo-more]');
    if (!this.body || !this.button) return;

    this.dialog = this.hasAttribute('data-popup') ? this.querySelector('[data-collection-seo-dialog]') : null;

    this.button.addEventListener('click', () => (this.dialog ? this.openPopup() : this.toggle()));
    this.setAttribute('data-clamped', '');
    this.sync();

    if (this.dialog) {
      this.dialog.querySelector('[data-collection-seo-close]')?.addEventListener('click', () => this.dialog.close());
      // A tap on the dimmed area outside the box lands on the dialog itself.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => this.button.focus({ preventScroll: true }));
    }

    // Re-measured on resize: the line count that fits changes with the width.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.sync());
      this.observer.observe(this.body);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /* scrollHeight is the full text, clientHeight the clamped box. 2px absorbs
     sub-pixel rounding. While expanded nothing is clipped by definition, and
     the button has to stay to offer "Read less". */
  sync() {
    if (this.hasAttribute('data-expanded')) return;
    const clipped = this.body.scrollHeight - this.body.clientHeight > 2;
    this.toggleAttribute('data-overflowing', clipped);
    this.button.hidden = !clipped;
  }

  toggle() {
    const expanded = this.toggleAttribute('data-expanded');
    this.button.setAttribute('aria-expanded', String(expanded));
    this.button.textContent = expanded ? this.dataset.lessLabel : this.dataset.moreLabel;

    // Collapsing a long text can leave the reader far below it; bring the
    // start of the copy back into view so they are not thrown down the page.
    if (!expanded && this.getBoundingClientRect().top < 0) {
      this.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  openPopup() {
    const content = this.dialog.querySelector('[data-collection-seo-dialog-body]');
    if (!content.hasChildNodes()) {
      content.append(...Array.from(this.body.childNodes, (node) => node.cloneNode(true)));
      content.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));

      // The copy's own first heading becomes the popup title, so it isn't
      // shown twice; the collection title stays as the fallback.
      const wrapper = content.querySelector('.metafield-rich_text_field') ?? content;
      const heading = wrapper.firstElementChild;
      if (heading?.matches('h2, h3, h4, h5, h6')) {
        this.dialog.querySelector('[data-collection-seo-dialog-title]').textContent = heading.textContent.trim();
        heading.remove();
      }
    }

    content.scrollTop = 0;
    this.dialog.showModal();
  }
}

if (!customElements.get('collection-seo-text')) {
  customElements.define('collection-seo-text', CollectionSeoText);
}
